import { useOne } from "@refinedev/core";
import Input from "components/input"
import { IOrgRoleForm, IPermission, PermissionAction } from "interfaces";
import { useEffect } from "react";
import { UseFormResetField } from "react-hook-form";
import { arrayCompare } from "utills/helpers";

interface PermissionProps {
  organizationId?: string;
  disabled?: boolean;
  resetField?: UseFormResetField<IOrgRoleForm>;
  data?: IPermission[];
  isOwner?: boolean;
  onChange?: (data: IPermission[]) => void;
}

const PERMISSION_ACTIONS_BY_ORDER = [
  PermissionAction.VIEW,
  PermissionAction.EDIT,
  PermissionAction.CREATE,
  PermissionAction.EXPORT,
  PermissionAction.DELETE,
];

function PermissionSkeleton() {
  return (
    <div className="py-4 sm:px-4 sm:grid sm:grid-cols-6 sm:gap-4 animate-pulse">
      <div className="text-sm sm:col-span-2">
        <div className="h-3 bg-gray-200 rounded-full w-40" />
      </div>
      <div className="grid grid-cols-2 gap-4 mt-6 sm:mt-0 sm:grid-cols-4 sm:col-span-4">
        <div className="w-fit flex items-center">
          <div className="h-3 bg-gray-200 rounded-full w-12"></div>
        </div>
        <div className="w-fit flex items-center">
          <div className="h-3 bg-gray-200 rounded-full w-12"></div>
        </div>
        <div className="w-fit flex items-center">
          <div className="h-3 bg-gray-200 rounded-full w-12"></div>
        </div>
        <div className="w-fit flex items-center">
          <div className="h-3 bg-gray-200 rounded-full w-12"></div>
        </div>
      </div>
    </div>
  )
}

export const Permissions = ({ disabled = false, data = [], organizationId, onChange, resetField, isOwner = false }: PermissionProps) => {
  const {
    data: orgPermissions,
    isLoading: isLoadingOrgPermissions,
    isFetching: isFetchingOrgPermissions,
  } = useOne<IPermission[]>({
    resource: isOwner ? "organization/permissions" : "organization/role",
    id: isOwner ? "maximum" : `${organizationId}/owner`,

    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!organizationId,
    } as any,
  });

  const orgPermissionsData = orgPermissions?.data ?? [];

  const _handleChangeAction = (
    selectedPermission: IPermission,
    action: PermissionAction[],
    isChecked: boolean
  ) => {
    const updatedPermissions = [...data];

    const pIndex = updatedPermissions.findIndex(
      (p) =>
        p.resource === selectedPermission.resource &&
        p.app === selectedPermission.app
    );

    if (pIndex < 0) return;

    if (isChecked) {
      const isCheckedViewAction =
        action.includes(PermissionAction.CREATE) ||
        action.includes(PermissionAction.EDIT) ||
        action.includes(PermissionAction.EXPORT) ||
        action.includes(PermissionAction.DELETE);
      updatedPermissions[pIndex].actions = [
        ...new Set([
          ...updatedPermissions[pIndex].actions,
          ...action,
          ...(isCheckedViewAction ? [PermissionAction.VIEW] : []),
        ]),
      ];
    } else {
      const filterActions = action.includes(PermissionAction.VIEW)
        ? [
            ...new Set([
              ...action,
              ...[
                PermissionAction.CREATE,
                PermissionAction.EDIT,
                PermissionAction.EXPORT,
                PermissionAction.DELETE,
              ],
            ]),
          ]
        : action;
      updatedPermissions[pIndex].actions = updatedPermissions[
        pIndex
      ].actions.filter((actions) => !filterActions.includes(actions));
    }

    onChange?.(updatedPermissions);
  };
  
  useEffect(() => {
    if (orgPermissions?.data && !disabled && resetField && !isFetchingOrgPermissions) {
      resetField("permissions", {defaultValue: orgPermissions?.data.map(permission => ({
        ...permission,
        actions: data.find(p => (p.resource === permission.resource && p.app === permission.app))?.actions ?? []
      }))});
    }
  }, [orgPermissions, isFetchingOrgPermissions]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-transparent divide-y divide-gray-100 sm:bg-magnolia-400 sm:rounded-lg sm:shadow-sm">
      <div className="hidden sm:p-4 sm:grid sm:grid-cols-6 sm:gap-4" hidden>
        <p className="text-base sm:col-span-2 font-semibold">
          Permission name
        </p>
        <p className="text-base sm:col-span-4 font-semibold">
          Action name
        </p>
      </div>
      {(isLoadingOrgPermissions || isFetchingOrgPermissions) 
      ? (
        <>
          <PermissionSkeleton />
          <PermissionSkeleton />
          <PermissionSkeleton />
        </>
      ) 
      : 
      orgPermissionsData.map((permission: IPermission) => {
        const formPermissionValues = data.find(item => item.resource === permission.resource && item.app === permission.app);
        return (
          <div className="py-4 sm:px-4 sm:grid sm:grid-cols-6 sm:gap-4" key={`${permission.app}:${permission.resource}`}>
            <p className="text-sm sm:col-span-2">{permission.name}</p>
            <div className="grid grid-cols-2 gap-4 mt-6 sm:mt-0 sm:grid-cols-6 sm:col-span-4">
              <div className="w-fit flex items-center">
                <Input
                  type="checkbox"
                  label="All"
                  id={`${permission.app}:${permission.resource}-all`}
                  checked={arrayCompare(permission.actions, formPermissionValues?.actions ?? []) ?? false}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => _handleChangeAction(permission, permission.actions, e.target.checked)}
                  disabled={disabled}
                />
              </div>
              {PERMISSION_ACTIONS_BY_ORDER.map((action) => (
                <div className="w-fit flex items-center" key={action}>
                  {permission.actions.includes(action) ? (
                    <Input
                      type="checkbox"
                      label={action}
                      id={`${permission.app}:${permission.resource}-${action}`}
                      checked={
                        formPermissionValues?.actions.includes(action) ?? false
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        _handleChangeAction(
                          permission,
                          [action],
                          e.target.checked
                        )
                      }
                      disabled={disabled}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              ))}
            </div>
          </div>
      )})}
    </div>
  )
}